// ============================================================================
// THE QUOTE PREFIX BELONGS TO THE BUSINESS (decision D5 = O1, 2026-10-05).
// ----------------------------------------------------------------------------
// Every Quote # ("HTB261001") and every generated title ("QUOTE HTB/HSB
// (1026)") starts with the business's quote prefix. A business that set one
// (tenant_profiles.quote_number_prefix, migration 091) gets it. A business
// that has not gets the initials of its business name: legal-form words such
// as Pty, Ltd and Limited are dropped, only letters count, the result is upper
// case and at most 8 letters. "Harbourside Test Builders Pty Ltd" gives HTB,
// and "RJ BIRD BUILDING PTY LTD" gives RBB, which is also what RJ Bird stores.
//
// Before this, a business with no prefix borrowed RJ Bird's compile-time
// "RBB", so another firm's quotes went out numbered and titled as RJ Bird's.
// There is no such fallback any more: with no stored prefix and no name, the
// answer is '' and the caller shows the number bare or an unresolved title.
//
// The backend runs the same rule from its own copy,
// backend/src/services/quote-number-prefix.js (the backend may not import
// from src/). src/__tests__/quote-number-prefix-parity.test.ts runs both over
// one table of names so they cannot drift apart.
// ============================================================================

/** The rule of tenant_profiles.quote_number_prefix (migration 091). */
export const QUOTE_PREFIX_RE = /^[A-Za-z0-9]{1,8}$/;

export const MAX_DERIVED_QUOTE_PREFIX_LENGTH = 8;

/** Legal-form words that say what kind of company it is, not which one. */
const LEGAL_FORM_WORDS = new Set([
    'PTY', 'LTD', 'LIMITED', 'PROPRIETARY', 'INC', 'INCORPORATED', 'LLC', 'CORP', 'CORPORATION',
]);

export type QuotePrefixSource = 'stored' | 'business-name' | 'none';

export type BusinessQuotePrefix = {
    /** '' when neither a stored prefix nor a business name gives one. */
    prefix: string;
    source: QuotePrefixSource;
};

type ProfileLike = {
    quoteNumberPrefix?: unknown;
    quote_number_prefix?: unknown;
    legalName?: unknown;
    legal_name?: unknown;
    tradingName?: unknown;
    trading_name?: unknown;
} | null | undefined;

/** The business's own prefix as stored, trimmed, or '' when none is valid. */
export function storedQuotePrefix(profile: ProfileLike): string {
    if (!profile || typeof profile !== 'object') return '';
    const raw = profile.quoteNumberPrefix ?? profile.quote_number_prefix;
    const prefix = typeof raw === 'string' ? raw.trim() : '';
    return QUOTE_PREFIX_RE.test(prefix) ? prefix : '';
}

/**
 * The initials of a business name: "Harbourside Test Builders Pty Ltd" gives
 * "HTB". '' when the name has no letters left to take.
 */
export function quotePrefixFromBusinessName(name: unknown): string {
    if (typeof name !== 'string') return '';
    const words = name
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        // "O'Brien" is one word, and "P/L" is "Pty Ltd" written short.
        .replace(/['’]/g, '')
        .replace(/\bP\s*\/\s*L\b/gi, ' ')
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean);
    return words
        .filter(word => !LEGAL_FORM_WORDS.has(word.toUpperCase()))
        .map(word => (/^[A-Za-z]/.test(word) ? word.charAt(0).toUpperCase() : ''))
        .join('')
        .slice(0, MAX_DERIVED_QUOTE_PREFIX_LENGTH);
}

/** The prefix this business's quotes carry, and where it came from. */
export function resolveBusinessQuotePrefix(profile: ProfileLike): BusinessQuotePrefix {
    const stored = storedQuotePrefix(profile);
    if (stored) return { prefix: stored, source: 'stored' };
    if (!profile || typeof profile !== 'object') return { prefix: '', source: 'none' };
    for (const name of [profile.legalName ?? profile.legal_name, profile.tradingName ?? profile.trading_name]) {
        const derived = quotePrefixFromBusinessName(name);
        if (derived) return { prefix: derived, source: 'business-name' };
    }
    return { prefix: '', source: 'none' };
}

/** resolveBusinessQuotePrefix(profile).prefix. */
export function effectiveQuoteNumberPrefix(profile: ProfileLike): string {
    return resolveBusinessQuotePrefix(profile).prefix;
}
